import type { ISpotlight } from "src/types/story";

import { Box, alpha } from "@mui/material";

import { CornerDot } from "./corner-dot";

interface MoveableRectangleProps {
  index: number;
  spotlight: ISpotlight;
  disabled: boolean;
  selected: boolean;
  onClick: () => void;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLElement>) => void;
  handleMouseUp: () => void;
}

export function MoveableRectangle({
  index,
  spotlight,
  onClick,
  disabled,
  selected,
  isDragging,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
}: MoveableRectangleProps) {

  return (
    <Box
      component="div"
      key={index}
      sx={{
        position: 'absolute',
        left: `${spotlight.x1 * 100}%`,
        top: `${spotlight.y1 * 100}%`,
        width: `${(spotlight.x2 - spotlight.x1) * 100}%`,
        height: `${(spotlight.y2 - spotlight.y1) * 100}%`,
        opacity: selected ? 1 : 0.5,
        pointerEvents: 'auto',
        cursor: !disabled ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        boxShadow: !disabled ? '0 0 0 1px rgba(0, 0, 0, 0.1)' : 'none',
        outline: !disabled ? '1px solid rgba(25, 118, 210, 0.3)' : 'none',
        outlineOffset: '1px',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
      }}
      onClick={onClick}
      onMouseDown={!disabled ? handleMouseDown : undefined}
      onMouseMove={!disabled ? handleMouseMove : undefined}
      onMouseUp={!disabled ? handleMouseUp : undefined}
      onTouchStart={!disabled ? (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY,
        });
        handleMouseDown(mouseEvent as any);
      } : undefined}
      onTouchMove={!disabled ? (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousemove', {
          clientX: touch.clientX,
          clientY: touch.clientY,
        });
        handleMouseMove(mouseEvent as any);
      } : undefined}
      onTouchEnd={!disabled ? (e: any) => {
        e.preventDefault();
        handleMouseUp();
      } : undefined}
    >
        <>
          <Box 
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: selected && !disabled ? '3px solid' : '3px dashed',
              borderColor: selected ? 'primary.main' : '#000',
              borderRadius: selected && !disabled ? 0 : '8px',
              pointerEvents: 'none',
              backgroundColor: (theme) => selected
              ? alpha(theme.palette.primary.main, 0.3)
              : alpha(theme.palette.secondary.main, 0.3),
            }}
          />
          {selected && !disabled && (
            <>
              <CornerDot position="nw" />
              <CornerDot position="ne" />
              <CornerDot position="sw" />
              <CornerDot position="se" />
          </>
        )}
      </>
    </Box>
  );
}